import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, HostListener, Input, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AppLanguage, I18nLocaleService, SUPPORTED_LANGUAGES } from '@core/i18n';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [AsyncPipe, TranslatePipe],
  template: `
    <div class="relative" [class]="hostClass">
      <button
        type="button"
        class="m-0 inline-flex appearance-none items-center gap-1.5 border-0 bg-transparent transition"
        [class]="buttonClass"
        [attr.aria-label]="'common.changeLanguage' | translate"
        [attr.aria-expanded]="isOpen"
        aria-haspopup="listbox"
        (click)="toggleMenu($event)"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="h-4 w-4 shrink-0"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span>{{ currentLabel }}</span>
      </button>

      @if (isOpen) {
        <div
          [class]="menuClass"
          role="listbox"
          [attr.aria-label]="'common.changeLanguage' | translate"
        >
          @for (language of languages; track language.code) {
            <button
              type="button"
              class="m-0 flex w-full appearance-none items-center justify-between gap-3 border-0 bg-transparent px-3 py-2 text-left text-sm transition hover:bg-gray-50"
              role="option"
              [attr.aria-selected]="(translateService.onLangChange | async)?.lang === language.code || translateService.currentLang() === language.code"
              (click)="selectLanguage(language.code, $event)"
            >
              <span>{{ language.name }}</span>
              <span class="text-xs font-medium text-gray-400">{{ language.label }}</span>
            </button>
          }
        </div>
      }
    </div>
  `,
})
export class LanguageSwitcherComponent {
  readonly translateService = inject(TranslateService);
  private readonly locale = inject(I18nLocaleService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly languages = SUPPORTED_LANGUAGES;

  @Input() hostClass = '';
  @Input() buttonClass = '';
  @Input() menuPlacement: 'top' | 'bottom' = 'bottom';

  isOpen = false;

  get menuClass(): string {
    const base =
      'absolute right-0 z-20 min-w-[9rem] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg';

    return this.menuPlacement === 'top'
      ? `${base} bottom-full right-0 mb-2`
      : `${base} mt-2`;
  }

  get currentLabel(): string {
    return (
      this.languages.find((language) => language.code === this.locale.currentLanguage)
        ?.label ?? 'EN'
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  async selectLanguage(code: AppLanguage, event: Event): Promise<void> {
    event.stopPropagation();
    this.isOpen = false;

    if (code !== this.locale.currentLanguage) {
      await firstValueFrom(this.translateService.use(code));
      this.locale.persistLanguage(code);
    }
  }
}
